import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEditable } from 'use-editable';
import { debounce } from 'underscore';

import { updateWorkingEvent } from '../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../store/types';
import { BrandliveEvent } from '../../../../../types/working-model';

import './event-name.scss';
import useRequiredFields from '../../../../../utils/use-required-fields';
import { CheckEventName } from '../../../../../connection/create-event';
import { showAlert } from '../../../../general-ui/alert/alert-service';
import classNames from 'classnames';
import { Tooltip } from '../../../../general-ui/tooltip/tooltip';

const EventName: React.FC = () => {

	const {
		CreateEventReducer: {
			workingEvent,
		},
		AuthReducer: {
			token,
			channels,
			user
		},
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();

	const [code, setCode] = useState('');
	const [initialized, setInitialized] = useState(false);
	const [active, setActive] = useState(false);
	const [error, setError] = useState('');

	const editorRef = useRef<HTMLDivElement | null>(null);
	const abortNameCheck = useRef<AbortController | null>(null);

	useEditable(editorRef, handleEditableChange);

	const {
		hasCompletedRequiredFields
	} = useRequiredFields(['name', 'duplicateName'], {
		name: (string: string) => {
			const valid = !!string.trim();
			if (!valid) setError('Invalid Event name');
			return valid;
		},
		duplicateName: (isDuplicate: boolean) => {
			if (isDuplicate) setError('Event name already used');
			return !isDuplicate;
		}
	});

	const channel = useMemo(() => {
		return channels.find((channel) => channel.channel === user!.active_channel);
	}, [channels, user]);

	function handleFocus() {
		setActive(true);
	}

	function handleEditableChange(text: string) {
		setCode(text);
	}

	async function handleBlur() {
		setActive(false);
		setCode(code);

		const nameExists = await checkName(code);
		if (hasCompletedRequiredFields({ name: code, duplicateName: nameExists })) {
			setError('');
			debounced(code);
		}
	}

	const checkName = useCallback(async (eventName: string) => {
		try {
			//use an abort controller here if someone types super fast, we don't want the 
			//fetch requests to arrive out of order and mess things up. 
			//so if the previous request is still in flight, abort the request and send a new one.
			if (abortNameCheck.current) abortNameCheck.current.abort();
			const [controller, checkEventNamePromise] = CheckEventName(token!, channel!.channel, eventName.trim(), workingEvent?.event ?? null);
			abortNameCheck.current = controller;

			const { exists } = await checkEventNamePromise;
			return exists;
		} catch (e) {
			console.error(e);
			showAlert({
				message: "Error Checking Event Name",
				description: "We were unable to check that event name. Please try again",
				type: "error"
			});
		}
	}, [channel, token, workingEvent?.event]);

	useEffect(() => {
		if (!initialized && workingEvent) {
			setInitialized(true);
			setCode(workingEvent.name);
		}
	}, [workingEvent, initialized]);

	const debounced = useRef(debounce((val: string) => {
		const updatedWithNewName: BrandliveEvent = {
			...workingEvent!,
			name: val.trim(),
		};
		dispatch(updateWorkingEvent(updatedWithNewName, token!));
	}, 300)).current;

	const titleEditor = (
		<div
			className={classNames("editable-text slim-container", { active })}
			ref={editorRef}
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			<h6>{code}</h6>
		</div>
	);

	return (
		<div className={classNames("event-title-editor", { error })}>
			{
				error ? (
					<Tooltip tooltip={error}>{titleEditor}</Tooltip>
				) : titleEditor
			}
		</div>
	);
};

export default EventName;
